import { useState, useEffect } from 'react'
import { Label, Input } from '@creanizer/ui'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@creanizer/ui'
import { Checkbox } from '@creanizer/ui'
import { Button } from '@creanizer/ui'
import { Users, UserMinus, RefreshCw, Upload, User, X, Save, FolderOpen, Trash2, Star } from 'lucide-react'
import { useCharacterStore } from '../../../../stores/characterStore'
import { useConsultationStore } from '../../../../stores/consultationStore'
import { apiClient } from '../../../../services/api'
import type { ConferenceSettings, ConferenceParticipant } from '@creanizer/types'

interface ConferenceSelectorProps {
  onSettingsChange: (settings: ConferenceSettings | null) => void
  conferenceSettings: ConferenceSettings | null
}

export function ConferenceSelector({ onSettingsChange, conferenceSettings }: ConferenceSelectorProps) {
  const characterStore = useCharacterStore()
  const consultationStore = useConsultationStore()
  const isStoreHydrated = useConsultationStore(state => state.isHydrated)
  const [enabled, setEnabled] = useState(conferenceSettings?.enabled || false)
  const [participants, setParticipants] = useState<ConferenceParticipant[]>(
    conferenceSettings?.participants || []
  )
  const [discussionStyle, setDiscussionStyle] = useState<ConferenceSettings['discussionStyle']>(
    conferenceSettings?.discussionStyle || 'casual'
  )
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [showPresetSave, setShowPresetSave] = useState(false)
  const [presetName, setPresetName] = useState('')
  const conferencePresets = useConsultationStore(state => state.conferencePresets)
  
  // 初期化時に既存の設定を適用（storeから復元）
  useEffect(() => {
    if (!isStoreHydrated) return
    
    // storeから保存された会議設定を復元
    const storedSettings = consultationStore.conferenceSettings
    if (storedSettings) {
      setEnabled(storedSettings.enabled)
      setParticipants(storedSettings.participants || [])
      setDiscussionStyle(storedSettings.discussionStyle || 'casual')
    } else if (conferenceSettings) {
      // propsからの設定があれば適用
      setEnabled(conferenceSettings.enabled)
      setParticipants(conferenceSettings.participants)
      setDiscussionStyle(conferenceSettings.discussionStyle)
    }
  }, [isStoreHydrated, consultationStore.conferenceSettings, conferenceSettings])

  // 会議モードの有効/無効切り替え
  const handleEnabledChange = (checked: boolean) => {
    setEnabled(checked)
    
    if (checked && participants.length >= 2) {
      // 会議モードを有効化
      const settings: ConferenceSettings = {
        enabled: true,
        participants,
        discussionStyle
      }
      console.log('Conference mode enabled with settings:', settings)
      onSettingsChange(settings)
      consultationStore.setConferenceSettings(settings)  // storeに保存
    } else if (!checked) {
      // 会議モードを無効化
      console.log('Conference mode disabled')
      onSettingsChange(null)
      consultationStore.setConferenceSettings(null)  // storeから削除
    } else if (checked && participants.length < 2) {
      // チェックを入れたが参加者が不足
      console.log('Conference mode checked but insufficient participants')
    }
  }

  // 参加者の追加
  const handleAddParticipant = async (characterId: string) => {
    if (participants.find(p => p.characterId === characterId)) {
      return // 既に追加済み
    }
    
    const character = characterStore.characters?.find(c => c.id === characterId)
    if (!character) return
    
    console.log('Adding participant:', character.result.name)
    setIsAnalyzing(true)
    
    try {
      // AI分析を取得（キャッシュがあればそれを使用）
      const cached = consultationStore.getCachedAnalysis(characterId)
      let settings = cached?.settings
      
      if (!cached) {
        // キャッシュがなければAI分析
        const response = await apiClient.analyzePersona(character.result)
        if (response.success && response.data) {
          const { reasoning, ...autoSettings } = response.data
          settings = autoSettings
          
          // 成功時のみキャッシュに保存
          consultationStore.setCachedAnalysis(characterId, {
            settings: autoSettings,
            reasoning
          })
        }
        // エラー時はデフォルト値を使用（キャッシュには保存しない）
      }
      
      console.log('ConferenceSelector sending characterData:', {
        name: character.result.name,
        hasBehavior: !!character.result.behavior,
        behaviorFirstPerson: character.result.behavior?.firstPerson,
        hasDirectSpeechPattern: !!character.result.behavior?.speechPattern,
        keys: Object.keys(character.result || {})
      })
      
      const newParticipant: ConferenceParticipant = {
        characterId,
        characterName: character.result.name || 'Unknown',
        characterData: character.result,
        role: 'participant',
        settings
      }
      
      const updatedParticipants = [...participants, newParticipant]
      setParticipants(updatedParticipants)
      
      // 2名以上で会議モード有効化可能
      if (enabled && updatedParticipants.length >= 2) {
        const settings: ConferenceSettings = {
          enabled: true,
          participants: updatedParticipants,
          discussionStyle
        }
        console.log('Updating conference settings after adding participant:', settings)
        onSettingsChange(settings)
        consultationStore.setConferenceSettings(settings)  // storeに保存
      } else {
        console.log('Not updating conference settings - enabled:', enabled, 'participant count:', updatedParticipants.length)
      }
    } catch (error) {
      console.error('参加者追加エラー:', error)
    } finally {
      setIsAnalyzing(false)
    }
  }

  // 参加者の削除
  const handleRemoveParticipant = (characterId: string) => {
    const updatedParticipants = participants.filter(p => p.characterId !== characterId)
    setParticipants(updatedParticipants)
    
    if (enabled && updatedParticipants.length >= 2) {
      const settings: ConferenceSettings = {
        enabled: true,
        participants: updatedParticipants,
        discussionStyle
      }
      onSettingsChange(settings)
      consultationStore.setConferenceSettings(settings)  // storeに保存
    } else if (updatedParticipants.length < 2) {
      // 参加者が2名未満になったら会議モード無効化
      setEnabled(false)
      onSettingsChange(null)
      consultationStore.setConferenceSettings(null)  // storeから削除
    }
  }

  // 議論スタイル変更
  const handleStyleChange = (style: ConferenceSettings['discussionStyle']) => {
    setDiscussionStyle(style)
    
    if (enabled && participants.length >= 2) {
      const settings: ConferenceSettings = {
        enabled: true,
        participants,
        discussionStyle: style
      }
      onSettingsChange(settings)
      consultationStore.setConferenceSettings(settings)  // storeに保存
    }
  }
  
  // 参加者のAI分析を再生成
  const handleRegenerateParticipantAnalysis = async (characterId: string) => {
    const participantIndex = participants.findIndex(p => p.characterId === characterId)
    if (participantIndex === -1) return
    
    const character = characterStore.characters?.find(c => c.id === characterId)
    if (!character) return
    
    setIsAnalyzing(true)
    
    try {
      // キャッシュをクリア
      consultationStore.setCachedAnalysis(characterId, null)
      
      // AI分析を再実行
      const response = await apiClient.analyzePersona(character.result)
      
      if (response.success && response.data) {
        const { reasoning, ...autoSettings } = response.data
        
        // キャッシュに保存
        consultationStore.setCachedAnalysis(characterId, {
          settings: autoSettings,
          reasoning
        })
        
        // 参加者情報を更新
        const updatedParticipants = [...participants]
        updatedParticipants[participantIndex] = {
          ...updatedParticipants[participantIndex],
          settings: autoSettings
        }
        setParticipants(updatedParticipants)
        
        // 会議設定を更新
        if (enabled && updatedParticipants.length >= 2) {
          const settings: ConferenceSettings = {
            enabled: true,
            participants: updatedParticipants,
            discussionStyle
          }
          onSettingsChange(settings)
          consultationStore.setConferenceSettings(settings)  // storeに保存
        }
      }
    } catch (error) {
      console.error('AI分析再生成エラー:', error)
    } finally {
      setIsAnalyzing(false)
    }
  }

  const handleIconUpload = (characterId: string, file: File) => {
    const reader = new FileReader()
    reader.onload = (event) => {
      const base64 = event.target?.result as string
      consultationStore.setCharacterIcon(characterId, base64)
    }
    reader.readAsDataURL(file)
  }

  const handleIconRemove = (characterId: string) => {
    consultationStore.clearCharacterIcon(characterId)
  }

  // プリセット保存
  const handleSavePreset = () => {
    if (!presetName.trim()) return

    const preset = consultationStore.saveConferencePreset(presetName.trim())
    if (preset) {
      setPresetName('')
      setShowPresetSave(false)
    }
  }

  // プリセット読み込み
  const handleLoadPreset = async (presetId: string) => {
    const preset = conferencePresets.find(p => p.id === presetId)
    if (!preset) return

    setIsAnalyzing(true)

    try {
      // プリセットの参加者を復元
      const newParticipants: ConferenceParticipant[] = []

      for (const presetParticipant of preset.participants) {
        // キャラクターデータを取得
        const character = characterStore.characters?.find(c => c.id === presetParticipant.characterId)
        if (!character) {
          console.warn(`Character not found: ${presetParticipant.characterName}`)
          continue
        }

        // AI分析を取得（キャッシュがあればそれを使用）
        const cached = consultationStore.getCachedAnalysis(presetParticipant.characterId)
        let settings = cached?.settings

        if (!cached) {
          // キャッシュがなければAI分析
          const response = await apiClient.analyzePersona(character.result)
          if (response.success && response.data) {
            const { reasoning, ...autoSettings } = response.data
            settings = autoSettings
            consultationStore.setCachedAnalysis(presetParticipant.characterId, {
              settings: autoSettings,
              reasoning
            })
          }
        }

        newParticipants.push({
          characterId: presetParticipant.characterId,
          characterName: presetParticipant.characterName,
          characterData: character.result,
          role: 'participant',
          settings
        })
      }

      setParticipants(newParticipants)
      setDiscussionStyle(preset.discussionStyle)

      // 2名以上ならすぐに会議モードを有効化
      if (newParticipants.length >= 2) {
        setEnabled(true)
        const settings: ConferenceSettings = {
          enabled: true,
          participants: newParticipants,
          discussionStyle: preset.discussionStyle
        }
        onSettingsChange(settings)
        consultationStore.setConferenceSettings(settings)
      }
    } catch (error) {
      console.error('プリセット読み込みエラー:', error)
    } finally {
      setIsAnalyzing(false)
    }
  }

  // プリセット削除
  const handleDeletePreset = (presetId: string) => {
    consultationStore.deleteConferencePreset(presetId)
  }

  return (
    <div className="space-y-4">
      {/* 会議モード有効化 */}
      <div className="flex items-center space-x-2">
        <Checkbox
          id="enableConference"
          checked={enabled}
          onCheckedChange={handleEnabledChange}
          disabled={participants.length < 2}
        />
        <Label htmlFor="enableConference" className="text-sm cursor-pointer">
          <Users className="inline-block w-4 h-4 mr-1" />
          会議モード（複数キャラクターでの議論）
        </Label>
      </div>

      {participants.length < 2 && (
        <div className="text-xs text-muted-foreground">
          ※会議モードには最低2名のキャラクターが必要です
        </div>
      )}

      {/* 参加者リスト */}
      <div className="space-y-2">
        <Label className="text-xs">会議参加者</Label>
        <div className="space-y-2">
          {participants.map((participant) => {
            const icon = consultationStore.getCharacterIcon(participant.characterId)
            const fileInputId = `icon-upload-${participant.characterId}`
            
            return (
              <div key={participant.characterId} className="flex items-center justify-between p-2 border rounded">
                <div className="flex items-center gap-3 flex-1">
                  {/* アイコン表示 */}
                  <div className="relative group">
                    {icon ? (
                      <img 
                        src={icon} 
                        alt={participant.characterName} 
                        className="w-10 h-10 rounded-full object-cover border-2 border-border"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center border-2 border-dashed border-border">
                        <User className="h-5 w-5 text-muted-foreground" />
                      </div>
                    )}
                    {/* ホバー時のオーバーレイ */}
                    <div className="absolute inset-0 rounded-full bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <input
                        id={fileInputId}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0]
                          if (file) handleIconUpload(participant.characterId, file)
                        }}
                      />
                      <label htmlFor={fileInputId} className="cursor-pointer">
                        <Upload className="h-4 w-4 text-white" />
                      </label>
                      {icon && (
                        <button
                          onClick={() => handleIconRemove(participant.characterId)}
                          className="ml-1"
                        >
                          <X className="h-4 w-4 text-white" />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium">{participant.characterName}</div>
                    {participant.settings && (
                      <div className="text-xs text-muted-foreground mt-1">
                        性格: {participant.settings.personalityStrength}/10, 
                        口調: {participant.settings.formality}/10
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => characterStore.toggleFavorite(participant.characterId)}
                  title={characterStore.isFavorite(participant.characterId) ? 'お気に入り解除' : 'お気に入り登録'}
                  className="h-8 w-8"
                >
                  <Star className={`h-3 w-3 ${characterStore.isFavorite(participant.characterId) ? 'fill-yellow-400 text-yellow-400' : ''}`} />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => handleRegenerateParticipantAnalysis(participant.characterId)}
                  disabled={isAnalyzing}
                  title="AI分析を再生成"
                  className="h-8 w-8"
                >
                  <RefreshCw className="h-3 w-3" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => handleRemoveParticipant(participant.characterId)}
                  className="h-8 w-8"
                >
                  <UserMinus className="h-3 w-3" />
                </Button>
              </div>
            </div>
            )
          })}
        </div>
      </div>

      {/* 参加者追加 */}
      {participants.length < 5 && (
        <div className="space-y-2">
          <Label className="text-xs">参加者を追加</Label>
          {!characterStore.characters || characterStore.characters.length === 0 ? (
            <div className="text-xs text-muted-foreground p-2 border rounded">
              保存済みキャラクターがありません
            </div>
          ) : (
            <Select
              onValueChange={handleAddParticipant}
              disabled={isAnalyzing || participants.length >= 5}
            >
              <SelectTrigger>
                <SelectValue placeholder="キャラクターを選択..." />
              </SelectTrigger>
              <SelectContent>
                {/* お気に入りを上部に表示 */}
                {characterStore.characters
                  ?.filter(c => !participants.find(p => p.characterId === c.id))
                  .slice()
                  .sort((a, b) => {
                    if (a.favorite && !b.favorite) return -1
                    if (!a.favorite && b.favorite) return 1
                    return 0
                  })
                  .map((character) => (
                    <SelectItem key={character.id} value={character.id}>
                      <span className="flex items-center gap-1">
                        {character.favorite && <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />}
                        {character.result.name || 'Unknown'}
                      </span>
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          )}
          {participants.length >= 5 && (
            <div className="text-xs text-muted-foreground">
              ※最大5名まで参加可能です
            </div>
          )}
        </div>
      )}

      {/* 議論スタイル */}
      <div className="space-y-2">
        <Label className="text-xs">議論スタイル</Label>
        <Select
          value={discussionStyle}
          onValueChange={handleStyleChange}
          disabled={!enabled}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="casual">カジュアル（自由な意見交換）</SelectItem>
            <SelectItem value="formal">フォーマル（順番に発言）</SelectItem>
            <SelectItem value="brainstorm">ブレインストーミング（アイデア重視）</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* 現在の設定サマリー */}
      {enabled && participants.length >= 2 && (
        <div className="p-3 bg-muted rounded-lg">
          <div className="text-xs font-semibold mb-2">会議設定</div>
          <div className="text-xs space-y-1">
            <div>参加者: {participants.map(p => p.characterName).join('、')}</div>
            <div>スタイル: {
              discussionStyle === 'casual' ? 'カジュアル' :
              discussionStyle === 'formal' ? 'フォーマル' : 'ブレインストーミング'
            }</div>
          </div>
        </div>
      )}

      {/* プリセット管理 */}
      <div className="space-y-2 border-t pt-4">
        <Label className="text-xs font-semibold flex items-center gap-1">
          <FolderOpen className="h-3 w-3" />
          プリセット
        </Label>

        {/* プリセット読み込み */}
        {conferencePresets.length > 0 && (
          <div className="space-y-1">
            {conferencePresets.map(preset => (
              <div key={preset.id} className="flex items-center justify-between p-2 border rounded text-xs">
                <div className="flex-1">
                  <div className="font-medium">{preset.name}</div>
                  <div className="text-muted-foreground">
                    {preset.participants.map(p => p.characterName).join('、')}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleLoadPreset(preset.id)}
                    disabled={isAnalyzing}
                    title="読み込み"
                    className="h-7 px-2"
                  >
                    <FolderOpen className="h-3 w-3" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDeletePreset(preset.id)}
                    title="削除"
                    className="h-7 px-2 text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* プリセット保存 */}
        {participants.length >= 2 && (
          showPresetSave ? (
            <div className="flex items-center gap-2">
              <Input
                value={presetName}
                onChange={(e) => setPresetName(e.target.value)}
                placeholder="プリセット名"
                className="flex-1 h-8 text-xs"
                onKeyPress={(e) => e.key === 'Enter' && handleSavePreset()}
              />
              <Button
                size="sm"
                onClick={handleSavePreset}
                disabled={!presetName.trim()}
                className="h-8"
              >
                <Save className="h-3 w-3 mr-1" />
                保存
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setShowPresetSave(false)
                  setPresetName('')
                }}
                className="h-8"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          ) : (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowPresetSave(true)}
              className="w-full h-8 text-xs"
            >
              <Save className="h-3 w-3 mr-1" />
              現在のメンバーをプリセットとして保存
            </Button>
          )
        )}

        {conferencePresets.length === 0 && participants.length < 2 && (
          <div className="text-xs text-muted-foreground p-2 text-center">
            2名以上のメンバーを追加するとプリセット保存が可能です
          </div>
        )}
      </div>
    </div>
  )
}